/**
 * Settings → Best sellers.
 *
 * The models the voice agent recommends when a customer asks what's popular,
 * in the order it should offer them.
 *
 * **Stated by the dealership, not computed from sales.** Aggregating the sales
 * table looks obvious and does not work: `sales.vehicle_id` is copied from
 * `quotes.vehicle_id`, which is nullable, and there is no API to attach a
 * vehicle afterwards — so a sale raised from a quote with no vehicle has no
 * model attribution, the count silently undercounts, and nothing in the answer
 * reveals it. Beyond the data, "best seller" on a sales call is a sales
 * position rather than a statistic: a dealer may want to push what they have
 * depth or margin in, and a computed figure would sometimes contradict that.
 *
 * Hence a rank, not a unit count — the agent never speaks a number it cannot
 * defend. The talking point is read aloud in the dealer's own words.
 */
import { useCallback, useEffect, useState } from 'react';
import { bestSellersService } from '../../lib/services';
import NewVehicleModelSelect from './NewVehicleModelSelect';

const input = {
  width: '100%',
  padding: '7px 10px',
  fontSize: 12,
  color: 'var(--text-primary)',
  background: 'var(--bg-glass)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-sm)',
  outline: 'none',
};

const label = { fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };

function Field({ title, hint, children, style }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <label style={label}>{title}</label>
      {children}
      {hint && <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

const EMPTY = { make: '', model: '', variant: '', talkingPoint: '' };

export default function BestSellersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState(null);
  const [pickerResetKey, setPickerResetKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await bestSellersService.list();
      const arr = Array.isArray(list) ? list : [];
      // Rank order, so the list reads the way the agent will speak it.
      arr.sort((a, b) => (a.displayRank ?? 0) - (b.displayRank ?? 0));
      setRows(arr);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not load best sellers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const act = async (key, fn) => {
    setBusy(key);
    setError(null);
    try { await fn(); } catch (e) {
      setError(e?.response?.data?.message || e.message || 'That action failed');
    } finally { setBusy(null); }
  };

  const add = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!form.make.trim() || !form.model.trim()) {
      setFormError('Pick a model from new inventory first.');
      return;
    }
    // Append at the end of the ACTIVE set. Ranks must be unique among active
    // rows (a partial unique index enforces it), so taking max+1 avoids a
    // collision without asking the operator to pick a number.
    const nextRank = rows.reduce((max, r) => (r.isActive === false ? max : Math.max(max, r.displayRank ?? 0)), 0) + 1;
    await act('create', async () => {
      await bestSellersService.create({
        make: form.make.trim(),
        model: form.model.trim(),
        variant: form.variant.trim() || null,
        displayRank: nextRank,
        talkingPoint: form.talkingPoint.trim() || null,
        vehicleId: null,   // matched against live stock at query time instead
        isActive: true,
      });
      setForm(EMPTY);
      setPickerResetKey((k) => k + 1);
      await load();
    });
  };

  const remove = (row) => {
    if (!window.confirm(`Remove ${row.make} ${row.model} from the agent's recommendations?`)) return;
    act(row.id, async () => { await bestSellersService.remove(row.id); await load(); });
  };

  const toggleActive = (row) =>
    act(row.id, async () => {
      await bestSellersService.update(row.id, { isActive: !(row.isActive !== false) });
      await load();
    });

  /**
   * Move a row up or down among the ACTIVE rows.
   *
   * Sent as one batched reorder: swapping two ranks with separate updates would
   * transiently put both on the same number and trip the unique index.
   */
  const move = (row, direction) => {
    const active = rows.filter((r) => r.isActive !== false);
    const index = active.findIndex((r) => r.id === row.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= active.length) return;

    const reordered = [...active];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    act(row.id, async () => {
      await bestSellersService.reorder(reordered.map((r, i) => ({ id: r.id, displayRank: i + 1 })));
      await load();
    });
  };

  if (loading) return <div className="text-sm text-muted">Loading best sellers…</div>;

  const active = rows.filter((r) => r.isActive !== false);
  const inactive = rows.filter((r) => r.isActive === false);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Best sellers</h2>
        <p className="text-sm text-muted" style={{ maxWidth: 620 }}>
          What the voice agent recommends when a customer asks what&apos;s popular,
          in the order it offers them. Availability is checked against live stock
          when the agent asks, so a model with nothing on the ground is still
          mentioned — the agent says so rather than skipping it.
        </p>
      </div>

      {error && <div className="text-sm" style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div>}

      {active.length === 0 ? (
        <div className="text-sm text-muted" style={{ marginBottom: 20 }}>
          Nothing configured. Until something is added here the agent has no
          recommendation to offer and will hand off to a consultant instead.
        </div>
      ) : (
        <div style={{ marginBottom: 20, display: 'grid', gap: 8 }}>
          {active.map((row, i) => (
            <div key={row.id} className="card" style={{ padding: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                <span
                  className="pill filled"
                  style={{ minWidth: 26, textAlign: 'center', flexShrink: 0 }}
                  title="The order the agent reads them in"
                >
                  {row.displayRank ?? i + 1}
                </span>
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {row.make} {row.model}
                    {row.variant && <span className="text-muted" style={{ fontWeight: 400 }}> {row.variant}</span>}
                  </div>
                  {row.talkingPoint && (
                    <div className="text-xs text-muted" style={{ marginTop: 2 }}>
                      &ldquo;{row.talkingPoint}&rdquo;
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button" className="btn small"
                    disabled={busy === row.id || i === 0}
                    onClick={() => move(row, -1)}
                    title="Move up"
                  >↑</button>
                  <button
                    type="button" className="btn small"
                    disabled={busy === row.id || i === active.length - 1}
                    onClick={() => move(row, 1)}
                    title="Move down"
                  >↓</button>
                  <button type="button" className="btn small" disabled={busy === row.id} onClick={() => toggleActive(row)}>
                    Hide
                  </button>
                  <button type="button" className="btn small danger" disabled={busy === row.id} onClick={() => remove(row)}>
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {inactive.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div className="text-xs text-muted" style={{ marginBottom: 6 }}>
            Hidden — kept but not offered by the agent
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {inactive.map((row) => (
              <div key={row.id} className="card" style={{ padding: 8, opacity: 0.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ flex: '1 1 200px', fontSize: 12 }}>
                    {row.make} {row.model} {row.variant || ''}
                  </span>
                  <button type="button" className="btn small" disabled={busy === row.id} onClick={() => toggleActive(row)}>
                    Show
                  </button>
                  <button type="button" className="btn small danger" disabled={busy === row.id} onClick={() => remove(row)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={add} style={{ maxWidth: 520 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Add a model</h3>

        <Field
          title="Model"
          hint="Searches new-vehicle stock only — type a couple of letters and pick a match."
          style={{ maxWidth: 340 }}
        >
          <NewVehicleModelSelect
            key={pickerResetKey}
            onSelect={({ make, model, variant }) => setForm((f) => ({ ...f, make, model, variant }))}
            placeholder="Triton"
          />
        </Field>

        <Field
          title="Talking point (optional)"
          hint="One line on why, in your words. The agent reads this out, so write it the way you'd say it."
        >
          <textarea
            style={{ ...input, minHeight: 56, resize: 'vertical', fontFamily: 'inherit' }}
            value={form.talkingPoint}
            onChange={set('talkingPoint')}
            placeholder="Great tray capacity and a five-year warranty."
          />
        </Field>

        {formError && <div className="text-sm" style={{ color: 'var(--danger)', marginBottom: 10 }}>{formError}</div>}

        <button type="submit" className="btn primary" disabled={busy === 'create'}>
          {busy === 'create' ? 'Adding…' : 'Add model'}
        </button>
      </form>
    </div>
  );
}
